"use client";

/**
 * Блок «Безопасность» в шторке своего профиля: смена собственного пароля.
 *
 * Контракт:
 *  - компонент владеет только двумя локальными полями и их валидацией;
 *    отправку наружу делает родитель через onSubmit(password) -> Promise<boolean>;
 *  - поля очищаются только когда onSubmit вернул true (успешный PUT /user.json);
 *  - блок — <form>: Enter в любом поле равен клику «Изменить пароль», как на
 *    app.oqylyq.kz; поэтому в другую форму его не вкладывать;
 *  - текущий пароль не запрашивается: бэкенд его не требует, смена идёт тем же
 *    PUT /user.json, что и остальной профиль;
 *  - вёрстка и тексты повторяют блок «Безопасность» на app.oqylyq.kz — ключи
 *    переводов (label-profile-password-*, label-new-password*, btn-change-password)
 *    уже есть во всех локалях i18n.
 */

import * as React from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EyeIcon, EyeOffIcon, LockerIcon } from "@/components/icons";

/* Бэкенд принимает пароль от 6 символов — та же граница, что и в подсказке hint-password. */
const MIN_PASSWORD_LENGTH = 6;

type PasswordFieldProps = {
  label: string;
  hint: string;
  placeholder: string;
  value: string;
  onChange: (next: string) => void;
  invalid: boolean;
  disabled: boolean;
};

const PasswordField: React.FC<PasswordFieldProps> = ({
  label,
  hint,
  placeholder,
  value,
  onChange,
  invalid,
  disabled,
}) => {
  const t = useTranslations();
  const [isVisible, setIsVisible] = React.useState(false);

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium block">{label}</label>
      <div className="text-xs text-muted-foreground">{hint}</div>
      <div className="relative">
        <Input
          type={isVisible ? "text" : "password"}
          autoComplete="new-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={invalid}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setIsVisible((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
          title={isVisible ? t("ui-hide-password") : t("ui-show-password")}
          disabled={disabled}
        >
          {isVisible ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
        </button>
      </div>
    </div>
  );
};

type Props = {
  onSubmit: (password: string) => Promise<boolean>;
  isSubmitting: boolean;
};

const ProfilePasswordSection: React.FC<Props> = ({ onSubmit, isSubmitting }) => {
  const t = useTranslations();

  const [password, setPassword] = React.useState("");
  const [confirmation, setConfirmation] = React.useState("");
  const [showErrors, setShowErrors] = React.useState(false);

  const isTooShort = password.length < MIN_PASSWORD_LENGTH;
  const isMismatched = password !== confirmation;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setShowErrors(true);

    if (isTooShort) {
      toast.error(t("hint-password"));
      return;
    }

    if (isMismatched) {
      toast.error(t("error-passwords-do-not-match"));
      return;
    }

    const saved = await onSubmit(password);

    if (saved) {
      setPassword("");
      setConfirmation("");
      setShowErrors(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 border-t pt-6"
    >
      <div className="flex items-start gap-3">
        <LockerIcon size={18} className="mt-1 shrink-0 text-muted-foreground" />
        <div>
          <div className="font-semibold">{t("label-profile-password-title")}</div>
          <div className="text-sm text-muted-foreground">
            {t("label-profile-password-description")}
          </div>
        </div>
      </div>

      <PasswordField
        label={t("label-new-password")}
        hint={t("hint-password")}
        placeholder={t("placeholder-new-password")}
        value={password}
        onChange={setPassword}
        invalid={showErrors && isTooShort}
        disabled={isSubmitting}
      />

      <PasswordField
        label={t("label-new-password-confirmation")}
        hint={t("hint-new-password")}
        placeholder={t("placeholder-new-password-confirmation")}
        value={confirmation}
        onChange={setConfirmation}
        invalid={showErrors && !isTooShort && isMismatched}
        disabled={isSubmitting}
      />

      <div>
        <Button
          type="submit"
          variant="outline"
          className="rounded-sm bg-white px-5"
          disabled={isSubmitting}
        >
          {isSubmitting ? t("ui-saving") : t("btn-change-password")}
        </Button>
      </div>
    </form>
  );
};

export default ProfilePasswordSection;
